import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { BusinessLineProvider } from './contexts/BusinessLineContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { Layout } from './components/Layout';
import { ErrorBoundary } from './components/ErrorBoundary';
import { AccountSuspendedScreen } from './components/AccountSuspendedScreen';
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { ClientsPage } from './pages/ClientsPage';
import { LeadsPage } from './pages/LeadsPage';
import { PipelinePage } from './pages/PipelinePage';
import { PoliciesPage } from './pages/PoliciesPage';
import { DirectoryPage } from './pages/DirectoryPage';
import { DocumentsPage } from './pages/DocumentsPage';
import { InmueblesPage } from './pages/InmueblesPage';
import { AgentsPage } from './pages/AgentsPage';
import { UsersPage } from './pages/UsersPage';
import { IntegrationsPage } from './pages/IntegrationsPage';
import { CommentAutomationPage } from './pages/CommentAutomationPage';
import { CampaignsPage } from './pages/CampaignsPage';
import { CalendarPage } from './pages/CalendarPage';
import { SimulatorPage } from './pages/SimulatorPage';
import { ReportsPage } from './pages/ReportsPage';
import { SettingsPage } from './pages/SettingsPage';
import { ScheduledNotificationsPage } from './pages/ScheduledNotificationsPage';
import { RegisterPage } from './pages/RegisterPage';
import { SugerenciasPage } from './pages/SugerenciasPage';
import { ActivityPage } from './pages/ActivityPage';
import { ErrorsPage } from './pages/ErrorsPage';
import { Toaster } from './components/ui/sonner';

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1a365d]"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.account_suspended) {
    return <AccountSuspendedScreen />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Layout>{children}</Layout>;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1a365d]"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<RegisterPage />} />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/pipeline" element={<ProtectedRoute allowedRoles={['admin', 'master', 'agente', 'asistente', 'developer', 'aspirante']}><PipelinePage /></ProtectedRoute>} />
      <Route path="/clients" element={<ProtectedRoute allowedRoles={['admin', 'master', 'agente', 'asistente', 'developer', 'asesor', 'viewer', 'aspirante']}><ClientsPage /></ProtectedRoute>} />
      <Route path="/leads" element={<ProtectedRoute allowedRoles={['admin', 'master', 'agente', 'asistente', 'developer', 'asesor', 'viewer', 'aspirante']}><LeadsPage /></ProtectedRoute>} />
      <Route path="/policies" element={<ProtectedRoute allowedRoles={['admin', 'master', 'agente', 'asistente', 'developer', 'viewer', 'aspirante']}><PoliciesPage /></ProtectedRoute>} />
      <Route path="/directory" element={<ProtectedRoute allowedRoles={['admin', 'master', 'agente', 'asistente', 'developer', 'asesor', 'aspirante']}><DirectoryPage /></ProtectedRoute>} />
      <Route path="/documents" element={<ProtectedRoute allowedRoles={['admin', 'master', 'agente', 'asistente', 'developer', 'aspirante']}><DocumentsPage /></ProtectedRoute>} />
      <Route path="/inmuebles" element={<ProtectedRoute allowedRoles={['admin', 'master']}><InmueblesPage /></ProtectedRoute>} />
      <Route path="/agents" element={<ProtectedRoute allowedRoles={['admin', 'master']}><AgentsPage /></ProtectedRoute>} />
      <Route path="/users" element={<ProtectedRoute allowedRoles={['admin', 'master']}><UsersPage /></ProtectedRoute>} />
      <Route path="/integrations" element={<ProtectedRoute allowedRoles={['admin', 'master', 'agente', 'asistente', 'developer', 'asesor', 'aspirante']}><IntegrationsPage /></ProtectedRoute>} />
      <Route path="/integrations/comment-automation" element={<ProtectedRoute allowedRoles={['admin', 'master', 'agente', 'asistente', 'developer', 'asesor', 'aspirante']}><CommentAutomationPage /></ProtectedRoute>} />
      <Route path="/campaigns" element={<ProtectedRoute allowedRoles={['admin', 'master', 'agente', 'asistente', 'developer', 'aspirante']}><CampaignsPage /></ProtectedRoute>} />
      <Route path="/calendar" element={<ProtectedRoute allowedRoles={['admin', 'master', 'agente', 'asistente', 'developer', 'aspirante']}><CalendarPage /></ProtectedRoute>} />
      <Route path="/simulator" element={<ProtectedRoute allowedRoles={['admin', 'master', 'agente', 'asistente', 'developer', 'aspirante']}><SimulatorPage /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute allowedRoles={['admin', 'master', 'viewer']}><ReportsPage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/settings/alerts" element={<ProtectedRoute><ScheduledNotificationsPage /></ProtectedRoute>} />
      <Route path="/sugerencias" element={<ProtectedRoute allowedRoles={['admin', 'master', 'agente', 'asistente', 'developer', 'asesor', 'aspirante']}><SugerenciasPage /></ProtectedRoute>} />
      <Route path="/actividad" element={<ProtectedRoute allowedRoles={['admin', 'master']}><ActivityPage /></ProtectedRoute>} />
      <Route path="/errors" element={<ProtectedRoute allowedRoles={['admin', 'master']}><ErrorsPage /></ProtectedRoute>} />

      {/* Redirect */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <BusinessLineProvider>
            <NotificationProvider>
              <BrowserRouter>
                <AppRoutes />
                <Toaster position="top-right" />
              </BrowserRouter>
            </NotificationProvider>
          </BusinessLineProvider>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
